import React from 'react';
import {
  Box,
  Tab,
  Tabs,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Pagination,
  Button,
} from '@mui/material';
import { useComandaActions } from '../../states/comandas/comanda.actions';
import { useEffect, useState, useMemo, useRef } from 'react';
import { IComanda } from '../../commons/types/crm';
import { toast } from 'react-toastify';
import { DivReunioes, PaperReunioes } from '../Reunioes';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridFilterInputValue,
  GridSortDirection,
} from '@mui/x-data-grid';
import { useRecoilState, useRecoilValue } from 'recoil';
import { comandaAtom } from '../../states/comandas/ComandaState';
import { esES, ptBR } from '@mui/material/locale';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { authAtom } from '../../states/usuarios/AuthState';
import { a11yProps, CustomTabPanel } from '../../components/Tabs/accordionAndTabPanel';
import { useTranslation } from '../../commons/useTranslasion';
import { useDateFormatters } from '../../utils/useDateFormatters';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';

export interface ComandaTemp {
  id: number;
  nomeContato: string;
  emailContato: string;
  cpfContato: string;
  idContato: number;
  plano: string;
  status: string;
  modeloComercial: string;
  dataInicio: string;
  dataFim: string;
  dataExtensao: string;
}

// Utility function for debouncing
const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set a timeout to update the debounced value after the specified delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clear the timeout if the value changes or the component unmounts
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Assessorados = () => {
  const CustomToolbar = ({ name }: { name: string }) => {
    return (
      <GridToolbarContainer>
        <GridToolbarExport
          csvOptions={{
            delimiter: ';',
            fileName: `${name}-${moment().format('DD-MM-YYYY')}`,
            utf8WithBom: true,
            fields: [
              'nomeContato',
              'emailContato',
              'cpfContato',
              'status',
              'modeloComercial',
              'dataInicio',
              'dataFim',
            ],
          }}
        />
      </GridToolbarContainer>
    );
  };

  const CustomToolbarWrapper = (props: any) => {
    return <CustomToolbar {...props} name="comandasAtivas" />;
  };

  const CustomToolbarHistorico = (props: any) => {
    return <CustomToolbar {...props} name="comandasHistorico" />;
  };

  const { translate, language } = useTranslation();
  const { formatarData } = useDateFormatters();
  const lang = language ?? 'pt-br';
  const location = useLocation();
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case 'es':
      locale = esES;
      break;
  }

  // Define a custom filter operator that can be used by all columns
  const customFilterOperator = {
    value: 'contains',
    getApplyFilterFn: (filterItem: any) => {
      return (params: any) => {
        return true; // A filtragem real será feita no servidor
      };
    },
    InputComponent: GridFilterInputValue,
    InputComponentProps: { type: 'text' },
  };

  // Define a date filter operator for date columns
  const dateFilterOperators = [
    {
      value: 'equals',
      getApplyFilterFn: (filterItem: any) => {
        return (params: any) => {
          return true; // A filtragem real será feita no servidor
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: { type: 'date' },
    },
    {
      value: 'before',
      getApplyFilterFn: (filterItem: any) => {
        return (params: any) => {
          return true; // A filtragem real será feita no servidor
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: { type: 'date' },
    },
    {
      value: 'after',
      getApplyFilterFn: (filterItem: any) => {
        return (params: any) => {
          return true; // A filtragem real será feita no servidor
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: { type: 'date' },
    },
  ];

  const columnsAssessorados: GridColDef[] = [
    {
      field: 'nomeContato',
      headerName: translate('nomeAssessorado'),
      width: 120,
      flex: 120,
      filterOperators: [customFilterOperator],
      valueGetter(params: any, row: ComandaTemp) {
        const comanda = row;
        return comanda.nomeContato || '';
      },
      renderCell: (params: any) => {
        const value = params.row?.nomeContato || translate('contatoSemNomeCadastrado');
        const valueCPF = params.row?.cpfContato || translate('contatoSemCpfCadastrado');
        return (
          <Tooltip title={valueCPF} arrow>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'emailContato',
      headerName: translate('emailAssessorado'),
      width: 160,
      flex: 160,
      valueGetter(params: any, row: ComandaTemp) {
        const comanda = row;
        return comanda.emailContato || '';
      },
    },
    {
      field: 'plano',
      headerName: translate('plano'),
      width: 50,
      flex: 50,
      valueGetter(params: any, row: ComandaTemp) {
        return row.plano || '-';
      },
    },
    {
      field: 'status.nome',
      headerName: translate('status'),
      width: 50,
      flex: 50,
      valueGetter(params: any, row: ComandaTemp) {
        const comanda = row;
        return comanda.status || '-';
      },
    },
    {
      field: 'modeloComercial',
      headerName: translate('modeloComercial'),
      width: 30,
      flex: 30,
    },
    {
      field: 'dataInicio',
      headerName: translate('dataInicio'),
      width: 80,
      flex: 80,
      filterOperators: dateFilterOperators,
      // Removemos o type: 'date' para manter consistência com dataFim
      renderCell: (params: any) => {
        const row = params.row;
        if (!row) return '-';

        return row.dataInicio ? formatarData(row.dataInicio) : '-';
      },
      sortComparator: (v1, v2) => {
        const date1 = v1 ? new Date(v1).getTime() : 0;
        const date2 = v2 ? new Date(v2).getTime() : 0;
        return date1 - date2;
      },
    },
    {
      field: 'dataFim',
      headerName: translate('dataFim'),
      width: 80,
      flex: 80,
      filterOperators: dateFilterOperators,
      // Removemos o type: 'date' para evitar o problema com valueGetter
      // e usamos renderCell para exibir o valor correto
      renderCell: (params: any) => {
        const row = params.row;
        if (!row) return '-';

        const dateValue = row.dataExtensao ? row.dataExtensao : row.dataFim;
        return dateValue ? formatarData(dateValue) : '-';
      },
      // Mantemos o sortComparator para ordenação correta
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id) as ComandaTemp;
        const row2 = param2.api.getRow(param2.id) as ComandaTemp;

        const date1 = row1.dataExtensao
          ? new Date(row1.dataExtensao || '').getTime()
          : new Date(row1.dataFim || '').getTime();

        const date2 = row2.dataExtensao
          ? new Date(row2.dataExtensao || '').getTime()
          : new Date(row2.dataFim || '').getTime();

        return date1 - date2;
      },
    },
    {
      field: '',
      headerName: translate('acoes'),
      width: 80,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: GridCellParams) => {
        let comanda = params.row as ComandaTemp;
        return (
          <>
            <Link
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                color: 'rgb(70, 70, 70)',
              }}
              to={'/assessorados/' + comanda?.idContato + '/comandas/' + comanda.id}
            >
              <EditIcon />
            </Link>
          </>
        );
      },
    },
  ];

  const auth = useRecoilValue(authAtom);
  const comandaActions = useComandaActions();
  const [comandasAtivas, setComandasAtivas] = useState<ComandaTemp[]>([]);
  const [comandasHistorico, setComandasHistorico] = useState<ComandaTemp[]>([]);
  const [tabValue, setTabValue] = useState<number>(0);
  const [historicoCarregado, setHistoricoCarregado] = useState<boolean>(false);
  const [tabLoading, setTabLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const search = useLocation().search;

  // Estados para visualização mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filtro, setFiltro] = useState<string>('');
  const [mostrarTodos, setMostrarTodos] = useState<boolean>(false);
  const history = useHistory();

  // Estados para paginação de comandas ativas
  const [paginaAtualAtivas, setPaginaAtualAtivas] = useState<number>(1);
  const [itensPorPaginaAtivas, setItensPorPaginaAtivas] = useState<number>(10);
  const [totalRegistrosAtivos, setTotalRegistrosAtivos] = useState<number>(0);

  // Estados para paginação de histórico
  const [paginaAtualHistorico, setPaginaAtualHistorico] = useState<number>(1);
  const [itensPorPaginaHistorico, setItensPorPaginaHistorico] = useState<number>(10);
  const [totalRegistrosHistorico, setTotalRegistrosHistorico] = useState<number>(0);

  // Estados para gerenciar os filtros com debounce
  const [filterValueAtivas, setFilterValueAtivas] = useState<{
    coluna?: string;
    valor?: string;
    operador?: string;
  }>({});
  const [filterValueHistorico, setFilterValueHistorico] = useState<{
    coluna?: string;
    valor?: string;
    operador?: string;
  }>({});

  // Estados para gerenciar a ordenação
  const [sortModelAtivas, setSortModelAtivas] = useState<{
    field?: string;
    sort?: GridSortDirection;
  }>({});
  const [sortModelHistorico, setSortModelHistorico] = useState<{
    field?: string;
    sort?: GridSortDirection;
  }>({});

  // Valores debounced para reduzir chamadas à API
  const debouncedFilterValueAtivas = useDebounce(filterValueAtivas, 500); // 500ms de delay
  const debouncedFilterValueHistorico = useDebounce(filterValueHistorico, 500); // 500ms de delay
  const debouncedFiltroMobile = useDebounce(filtro, 800); // 800ms de delay para busca mobile

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setTabLoading(true);

    setTimeout(() => {
      if (newValue === 1 && !historicoCarregado) {
        buscarHistorico();
        setHistoricoCarregado(true);
      } else {
        setTabLoading(false);
      }
    }, 500);
  };

  const buscaInicial = async (
    pagina = paginaAtualAtivas,
    tamanhoPagina = itensPorPaginaAtivas,
    coluna?: string,
    valor?: string,
    operador?: string,
    ordenacaoColuna?: string,
    ordenacaoDirecao?: GridSortDirection,
  ) => {
    try {
      console.log(ordenacaoDirecao);

      setLoading(true);
      const searchParams = new URLSearchParams(search);
      const textoBusca = searchParams.get('busca');
      let query = textoBusca ? `?TextoBusca=${textoBusca}&` : '?';

      // Adicionar parâmetros de paginação
      query += `Pagina=${pagina}&TamanhoPagina=${tamanhoPagina}`;

      // Adicionar parâmetros de filtro
      if (coluna && valor && operador) {
        query += `&FiltroColuna=${coluna}&FiltroValor=${valor}&FiltroOperador=${operador}`;
      }

      // Adicionar parâmetros de ordenação
      if (ordenacaoColuna && ordenacaoDirecao) {
        query += `&FiltroColuna=${ordenacaoColuna}&OrdenacaoDirecao=${ordenacaoDirecao}`;
      }

      const data = await comandaActions.list(false, query);
      const result = data.resultado.itens;
      const totalRegistros = data.resultado.total;

      setComandasAtivas(result as ComandaTemp[]);
      setTotalRegistrosAtivos(totalRegistros);
    } catch (error) {
      toast.error(JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  const buscarHistorico = async (
    pagina = paginaAtualHistorico,
    tamanhoPagina = itensPorPaginaHistorico,
    coluna?: string,
    valor?: string,
    operador?: string,
    ordenacaoColuna?: string,
    ordenacaoDirecao?: GridSortDirection,
  ) => {
    try {
      setLoading(true);
      const searchParams = new URLSearchParams(search);
      const textoBusca = searchParams.get('busca');
      let query = textoBusca ? `?TextoBusca=${textoBusca}&historico=true` : '?historico=true';

      // Adicionar parâmetros de paginação
      query += `&Pagina=${pagina}&TamanhoPagina=${tamanhoPagina}`;

      // Adicionar parâmetros de filtro
      if (coluna && valor && operador) {
        query += `&FiltroColuna=${coluna}&FiltroValor=${valor}&FiltroOperador=${operador}`;
      }

      // Adicionar parâmetros de ordenação
      if (ordenacaoColuna && ordenacaoDirecao) {
        query += `&FiltroColuna=${ordenacaoColuna}&OrdenacaoDirecao=${ordenacaoDirecao}`;
      }

      const data = await comandaActions.list(false, query);
      const result = data.resultado.itens;
      const totalRegistros = data.resultado.total;

      setComandasHistorico(result as ComandaTemp[]);
      setTotalRegistrosHistorico(totalRegistros);
    } catch (error) {
      toast.error(JSON.stringify(error));
    } finally {
      setLoading(false);
      setTabLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const buscaParam = params.get('busca');

    // Sincronizar campo de busca mobile com URL
    if (buscaParam && buscaParam !== filtro) {
      setFiltro(buscaParam);
    } else if (!buscaParam && filtro) {
      setFiltro('');
    }

    if (tabParam === '1') {
      setTabValue(1);
      if (!historicoCarregado) {
        buscarHistorico(1, 5);
      }
    } else {
      buscaInicial(1, 5);
    }
  }, [location.search]);

  useEffect(() => {
    buscaInicial();
  }, [search]);

  // Efeito para aplicar o filtro debounced para comandas ativas
  useEffect(() => {
    if (
      debouncedFilterValueAtivas.coluna &&
      debouncedFilterValueAtivas.valor &&
      debouncedFilterValueAtivas.operador
    ) {
      buscaInicial(
        paginaAtualAtivas,
        itensPorPaginaAtivas,
        debouncedFilterValueAtivas.coluna,
        debouncedFilterValueAtivas.valor,
        debouncedFilterValueAtivas.operador,
        sortModelAtivas.field,
        sortModelAtivas.sort,
      );
    }
  }, [debouncedFilterValueAtivas, sortModelAtivas]);

  // Efeito para aplicar o filtro debounced para comandas históricas
  useEffect(() => {
    if (
      debouncedFilterValueHistorico.coluna &&
      debouncedFilterValueHistorico.valor &&
      debouncedFilterValueHistorico.operador
    ) {
      buscarHistorico(
        paginaAtualHistorico,
        itensPorPaginaHistorico,
        debouncedFilterValueHistorico.coluna,
        debouncedFilterValueHistorico.valor,
        debouncedFilterValueHistorico.operador,
        sortModelHistorico.field,
        sortModelHistorico.sort,
      );
    }
  }, [debouncedFilterValueHistorico, sortModelHistorico]);

  // Efeito para aplicar a ordenação para comandas ativas
  useEffect(() => {
    if (sortModelAtivas.field && sortModelAtivas.sort) {
      buscaInicial(
        paginaAtualAtivas,
        itensPorPaginaAtivas,
        filterValueAtivas.coluna,
        filterValueAtivas.valor,
        filterValueAtivas.operador,
        sortModelAtivas.field,
        sortModelAtivas.sort,
      );
    }
  }, [sortModelAtivas]);

  // Efeito para aplicar a ordenação para comandas históricas
  useEffect(() => {
    if (sortModelHistorico.field && sortModelHistorico.sort) {
      buscarHistorico(
        paginaAtualHistorico,
        itensPorPaginaHistorico,
        filterValueHistorico.coluna,
        filterValueHistorico.valor,
        filterValueHistorico.operador,
        sortModelHistorico.field,
        sortModelHistorico.sort,
      );
    }
  }, [sortModelHistorico]);

  // Função para realizar busca mobile via API
  const realizarBuscaMobile = (termo: string) => {
    if (termo.trim()) {
      // Atualizar URL com o termo de busca
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('busca', termo.trim());
      history.push(`${location.pathname}?${currentParams.toString()}`);
    } else {
      // Remover parâmetro de busca se termo estiver vazio
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete('busca');
      const newSearch = currentParams.toString();
      history.push(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    }
  };

  // Filtrar comandas baseado no termo de busca (apenas para fallback quando não há busca via API)
  const filtrarComandas = (comandas: ComandaTemp[], termo: string) => {
    if (!termo) return comandas;

    const termoLower = termo.toLowerCase();
    return comandas.filter(
      (comanda) =>
        (comanda.nomeContato || '').toLowerCase().includes(termoLower) ||
        (comanda.emailContato || '').toLowerCase().includes(termoLower) ||
        (comanda.status || '').toLowerCase().includes(termoLower) ||
        (comanda.modeloComercial || '').toLowerCase().includes(termoLower),
    );
  };

  // Comandas filtradas para tabela atual
  const comandasAtivasFiltradas = filtrarComandas(comandasAtivas, filtro);
  const comandasHistoricoFiltradas = filtrarComandas(comandasHistorico, filtro);

  // Total de páginas para paginação
  const totalPaginasAtivas = Math.ceil(comandasAtivasFiltradas.length / itensPorPaginaAtivas);
  const totalPaginasHistorico = Math.ceil(
    comandasHistoricoFiltradas.length / itensPorPaginaHistorico,
  );

  // Efeito para busca mobile com debounce
  useEffect(() => {
    if (isMobile && debouncedFiltroMobile !== undefined) {
      realizarBuscaMobile(debouncedFiltroMobile);
    }
  }, [debouncedFiltroMobile, isMobile]);

  // Resetar página quando alterna entre mostrar todos/paginar
  useEffect(() => {
    // Resetar ambas as paginações apenas quando mostrarTodos muda
    setPaginaAtualAtivas(1);
    setPaginaAtualHistorico(1);

    // Recarregar dados quando mostrarTodos mudar
    if (tabValue === 0) {
      buscaInicial(1, itensPorPaginaAtivas);
    } else if (tabValue === 1 && historicoCarregado) {
      buscarHistorico(1, itensPorPaginaHistorico);
    }
  }, [mostrarTodos]);

  // Itens a exibir na página atual (quando mostrarTodos está ativo, fazemos paginação no cliente)
  const comandasAtivasExibidas = useMemo(() => {
    if (mostrarTodos) {
      const inicio = (paginaAtualAtivas - 1) * itensPorPaginaAtivas;
      const fim = inicio + itensPorPaginaAtivas;
      return comandasAtivasFiltradas.slice(inicio, fim);
    }

    // Quando não estiver mostrando todos, usar diretamente os dados da API que já vêm paginados
    return comandasAtivas;
  }, [
    comandasAtivas,
    comandasAtivasFiltradas,
    paginaAtualAtivas,
    itensPorPaginaAtivas,
    mostrarTodos,
  ]);

  const comandasHistoricoExibidas = useMemo(() => {
    if (mostrarTodos) {
      const inicio = (paginaAtualHistorico - 1) * itensPorPaginaHistorico;
      const fim = inicio + itensPorPaginaHistorico;
      return comandasHistoricoFiltradas.slice(inicio, fim);
    }

    // Quando não estiver mostrando todos, usar diretamente os dados da API que já vêm paginados
    return comandasHistorico;
  }, [
    comandasHistorico,
    comandasHistoricoFiltradas,
    paginaAtualHistorico,
    itensPorPaginaHistorico,
    mostrarTodos,
  ]);

  // Função para trocar de página com rolagem suave ao topo
  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Atualizar a página correta com base na aba ativa
    if (tabValue === 0) {
      setPaginaAtualAtivas(value);
      buscaInicial(value, itensPorPaginaAtivas);
    } else if (tabValue === 1) {
      setPaginaAtualHistorico(value);
      buscarHistorico(value, itensPorPaginaHistorico);
    }
  };

  // Renderização de cards para visualização mobile das comandas
  const renderMobileCards = (comandas: ComandaTemp[], isHistorico: boolean) => {
    return (
      <Box sx={{ py: 1, px: 0 }}>
        {/* Campo de busca para mobile */}
        <Box sx={{ mb: 2, px: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder={translate('buscar')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ backgroundColor: 'white', borderRadius: '8px' }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                realizarBuscaMobile(filtro);
              }
            }}
          />
        </Box>

        {/* Mensagem de carregando ou sem resultados */}
        {loading || (isHistorico ? tabValue === 1 && tabLoading : tabValue === 0 && tabLoading) ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{translate('carregando')}</Typography>
          </Box>
        ) : (
          (isHistorico
            ? comandasHistoricoExibidas.length === 0
            : comandasAtivasExibidas.length === 0) && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {translate('nenhumResultadoEncontrado')}
              </Typography>
            </Box>
          )
        )}

        {/* Renderização dos cards */}
        {(isHistorico ? comandasHistoricoExibidas : comandasAtivasExibidas).map((comanda) => {
          // Formatar datas
          const dataInicio = formatarData(comanda.dataInicio);
          const dataFim = comanda.dataExtensao
            ? formatarData(comanda.dataExtensao)
            : formatarData(comanda.dataFim);

          return (
            <Card
              key={comanda.id}
              sx={{
                mb: 2,
                borderRadius: '10px',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
                width: '100%',
                mx: 0,
              }}
            >
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                {/* Nome do assessorado */}
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {comanda.nomeContato || translate('contatoSemNomeCadastrado')}
                </Typography>

                {/* Status */}
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    label={comanda.status || ''}
                    color="primary"
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        padding: '4px 8px',
                      },
                    }}
                  />
                </Box>

                {/* Detalhes em colunas */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {/* Coluna 1 */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {translate('modeloComercial')}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {comanda.modeloComercial || '-'}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 1, fontWeight: 'bold' }}
                    >
                      {translate('plano')}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {comanda.plano || '-'}
                    </Typography>
                  </Box>

                  {/* Coluna 2 */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {translate('dataInicio')}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {dataInicio}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 1, fontWeight: 'bold' }}
                    >
                      {translate('dataFim')}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {dataFim}
                    </Typography>
                  </Box>
                </Box>

                {/* Campo de e-mail em linha separada (largura total) */}
                <Box sx={{ mt: 1.5, width: '100%' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {translate('emailAssessorado')}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {comanda.emailContato || '-'}
                  </Typography>
                </Box>

                {/* Link de edição */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'rgb(70, 70, 70)',
                      textDecoration: 'none',
                    }}
                    to={'/assessorados/' + comanda.idContato + '/comandas/' + comanda.id}
                  >
                    <EditIcon sx={{ mr: 0.5 }} />
                    <Typography variant="button">{translate('acoes')}</Typography>
                  </Link>
                </Box>
              </CardContent>
            </Card>
          );
        })}

        {/* Paginação e opção de mostrar todos */}
        {((isHistorico ? totalPaginasHistorico : totalPaginasAtivas) > 1 || mostrarTodos) && (
          <Stack spacing={1} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            {!mostrarTodos && (
              <Pagination
                count={isHistorico ? totalPaginasHistorico : totalPaginasAtivas}
                page={isHistorico ? paginaAtualHistorico : paginaAtualAtivas}
                onChange={handleChangePage}
                color="primary"
                size="medium"
                siblingCount={0}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                maxWidth: '400px',
                mt: 1,
              }}
            >
              {!mostrarTodos ? (
                <Typography variant="caption" color="text.secondary">
                  {translate('pagina')} {isHistorico ? paginaAtualHistorico : paginaAtualAtivas}{' '}
                  {translate('de')} {isHistorico ? totalPaginasHistorico : totalPaginasAtivas}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {translate('exibindo')}{' '}
                  {isHistorico ? comandasHistoricoFiltradas.length : comandasAtivasFiltradas.length}{' '}
                  {translate('itens')}
                </Typography>
              )}

              <Button
                size="small"
                color="primary"
                variant="text"
                onClick={() => setMostrarTodos(!mostrarTodos)}
                sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
              >
                {mostrarTodos ? translate('mostrarPaginado') : translate('mostrarTodos')}
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <DivReunioes>
      <PaperReunioes elevation={3}>
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              margin: isMobile ? '0px 0px !important' : '0px 25px !important',
            }}
          >
            <Tabs value={tabValue} onChange={handleChangeTab} aria-label="tabs comandas">
              <Tab label={translate('comandasAtivas')} {...a11yProps(0)} />
              <Tab label={translate('historico')} {...a11yProps(1)} />
            </Tabs>
          </Box>
          {/* Aba de Comandas Ativas */}
          {isMobile ? (
            // Versão com padding lateral reduzido para mobile
            <div
              role="tabpanel"
              hidden={tabValue !== 0}
              id={`simple-tabpanel-0`}
              aria-labelledby={`simple-tab-0`}
            >
              {tabValue === 0 && (
                <Box sx={{ py: 1, px: 0, mx: -2 }}>{renderMobileCards(comandasAtivas, false)}</Box>
              )}
            </div>
          ) : (
            // Versão padrão para desktop
            <CustomTabPanel value={tabValue} index={0}>
              <ThemeProvider theme={createTheme({}, locale)}>
                <DataGrid
                  rows={comandasAtivas}
                  columns={columnsAssessorados}
                  loading={loading || (tabValue === 0 && tabLoading)}
                  sx={{
                    borderRadius: '10px',
                    minHeight: '700px',
                    height: 'auto',
                  }}
                  slots={{
                    toolbar: CustomToolbarHistorico,
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: itensPorPaginaAtivas,
                        page: paginaAtualAtivas - 1,
                      },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  paginationMode="server"
                  paginationModel={{ pageSize: itensPorPaginaAtivas, page: paginaAtualAtivas - 1 }}
                  rowCount={totalRegistrosAtivos}
                  onPaginationModelChange={(model) => {
                    const novaPagina = model.page + 1;
                    const novoTamanho = model.pageSize;

                    setPaginaAtualAtivas(novaPagina);
                    setItensPorPaginaAtivas(novoTamanho);

                    buscaInicial(
                      novaPagina,
                      novoTamanho,
                      filterValueAtivas.coluna,
                      filterValueAtivas.valor,
                      filterValueAtivas.operador,
                      sortModelAtivas.field,
                      sortModelAtivas.sort,
                    );
                  }}
                  disableRowSelectionOnClick
                  filterMode="server"
                  onFilterModelChange={(model) => {
                    // Captura os parâmetros de filtro
                    const filtroAtual = model.items[0];
                    if (filtroAtual) {
                      const coluna = filtroAtual.field;
                      const valor = filtroAtual.value;
                      const operador = filtroAtual.operator;

                      // Atualiza o estado do filtro (será debounced)
                      setFilterValueAtivas({ coluna, valor, operador });

                      // Se não houver valor, limpa o filtro e faz a busca normal
                      if (!valor) {
                        buscaInicial(
                          paginaAtualAtivas,
                          itensPorPaginaAtivas,
                          undefined,
                          undefined,
                          undefined,
                          sortModelAtivas.field,
                          sortModelAtivas.sort,
                        );
                      }
                    } else {
                      // Se não houver filtro, faz a busca normal
                      setFilterValueAtivas({});
                      buscaInicial(
                        paginaAtualAtivas,
                        itensPorPaginaAtivas,
                        undefined,
                        undefined,
                        undefined,
                        sortModelAtivas.field,
                        sortModelAtivas.sort,
                      );
                    }
                  }}
                  sortingMode="server"
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const { field, sort } = model[0];
                      setSortModelAtivas({ field, sort });
                    } else {
                      setSortModelAtivas({});
                      buscaInicial(
                        paginaAtualAtivas,
                        itensPorPaginaAtivas,
                        filterValueAtivas.coluna,
                        filterValueAtivas.valor,
                        filterValueAtivas.operador,
                      );
                    }
                  }}
                />
              </ThemeProvider>
            </CustomTabPanel>
          )}

          {/* Aba de Histórico */}
          {isMobile ? (
            // Versão com padding lateral reduzido para mobile
            <div
              role="tabpanel"
              hidden={tabValue !== 1}
              id={`simple-tabpanel-1`}
              aria-labelledby={`simple-tab-1`}
            >
              {tabValue === 1 && (
                <Box sx={{ py: 1, px: 0, mx: -2 }}>
                  {renderMobileCards(comandasHistorico, true)}
                </Box>
              )}
            </div>
          ) : (
            // Versão padrão para desktop
            <CustomTabPanel value={tabValue} index={1}>
              <ThemeProvider theme={createTheme({}, locale)}>
                <DataGrid
                  rows={comandasHistorico}
                  columns={columnsAssessorados}
                  loading={loading || (tabValue === 1 && tabLoading)}
                  sx={{
                    borderRadius: '10px',
                    minHeight: '700px',
                    height: 'auto',
                  }}
                  slots={{
                    toolbar: CustomToolbarHistorico,
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: itensPorPaginaHistorico,
                        page: paginaAtualHistorico - 1,
                      },
                    },
                  }}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  paginationMode="server"
                  paginationModel={{
                    pageSize: itensPorPaginaHistorico,
                    page: paginaAtualHistorico - 1,
                  }}
                  rowCount={totalRegistrosHistorico}
                  onPaginationModelChange={(model) => {
                    const novaPagina = model.page + 1;
                    const novoTamanho = model.pageSize;

                    setPaginaAtualHistorico(novaPagina);
                    setItensPorPaginaHistorico(novoTamanho);

                    buscarHistorico(
                      novaPagina,
                      novoTamanho,
                      filterValueHistorico.coluna,
                      filterValueHistorico.valor,
                      filterValueHistorico.operador,
                      sortModelHistorico.field,
                      sortModelHistorico.sort,
                    );
                  }}
                  disableRowSelectionOnClick
                  filterMode="server"
                  onFilterModelChange={(model) => {
                    // Captura os parâmetros de filtro
                    const filtroAtual = model.items[0];
                    if (filtroAtual) {
                      const coluna = filtroAtual.field;
                      const valor = filtroAtual.value;
                      const operador = filtroAtual.operator;

                      // Atualiza o estado do filtro (será debounced)
                      setFilterValueHistorico({ coluna, valor, operador });

                      // Se não houver valor, limpa o filtro e faz a busca normal
                      if (!valor) {
                        buscarHistorico(
                          paginaAtualHistorico,
                          itensPorPaginaHistorico,
                          undefined,
                          undefined,
                          undefined,
                          sortModelHistorico.field,
                          sortModelHistorico.sort,
                        );
                      }
                    } else {
                      // Se não houver filtro, faz a busca normal
                      setFilterValueHistorico({});
                      buscarHistorico(
                        paginaAtualHistorico,
                        itensPorPaginaHistorico,
                        undefined,
                        undefined,
                        undefined,
                        sortModelHistorico.field,
                        sortModelHistorico.sort,
                      );
                    }
                  }}
                  sortingMode="server"
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const { field, sort } = model[0];
                      setSortModelHistorico({ field, sort });
                    } else {
                      setSortModelHistorico({});
                      buscarHistorico(
                        paginaAtualHistorico,
                        itensPorPaginaHistorico,
                        filterValueHistorico.coluna,
                        filterValueHistorico.valor,
                        filterValueHistorico.operador,
                      );
                    }
                  }}
                />
              </ThemeProvider>
            </CustomTabPanel>
          )}
        </Box>
      </PaperReunioes>
    </DivReunioes>
  );
};

export default Assessorados;
